import React, { createContext, useContext, useRef, useCallback, useState } from "react";

const AutomationContext = createContext(null);

export function AutomationProvider({ children }) {
  const registryRef = useRef(new Map());
  const [currentPage, setCurrentPage] = useState(null);
  const [pageState, setPageState] = useState({});

  const registerElement = useCallback((automationId, elementRef, metadata) => {
    registryRef.current.set(automationId, { ref: elementRef, ...metadata });
    return () => registryRef.current.delete(automationId);
  }, []);

  const updatePageState = useCallback((page, state) => {
    setCurrentPage(page);
    setPageState(prev => ({ ...prev, [page]: state }));
  }, []);

  const getPageSnapshot = useCallback(() => {
    const elements = [];
    registryRef.current.forEach((value, key) => {
      elements.push({
        id: key,
        type: value.type,
        label: value.label,
        state: value.getState?.() || null,
        visible: value.ref?.current?.offsetParent !== null,
        actions: value.actions || ["click"],
      });
    });
    return {
      page: currentPage,
      pageState: pageState[currentPage] || {},
      elements: elements.filter(e => e.visible),
      url: window.location.pathname + window.location.hash,
      timestamp: new Date().toISOString(),
    };
  }, [currentPage, pageState]);

  const executeAction = useCallback(async (automationId, action, params) => {
    const element = registryRef.current.get(automationId);
    if (!element) {
      const domEl = document.querySelector(`[data-automation-id="${automationId}"]`);
      if (domEl) {
        if (action === "click") { domEl.click(); return { success: true, action: "clicked", element: automationId }; }
        if (action === "scroll_to") { domEl.scrollIntoView({ behavior: "smooth", block: "center" }); return { success: true, action: "scrolled_to", element: automationId }; }
        if (action === "read") { return { success: true, action: "read", element: automationId, value: domEl.textContent || "" }; }
      }
      return { success: false, error: `Element "${automationId}" not found` };
    }

    if (window.__qnnealVoiceActionLabel) {
      const labels = { click: "Clicking", fill: "Typing into", select: "Selecting in", toggle: "Toggling", scroll_to: "Scrolling to", read: "Reading" };
      window.__qnnealVoiceActionLabel(`${labels[action] || action} ${element.label || automationId}...`);
    }

    try {
      const ref = element.ref?.current;
      switch (action) {
        case "click":
          if (ref) {
            ref.style.outline = "3px solid #00d4aa";
            ref.style.outlineOffset = "2px";
            setTimeout(() => { if (ref) { ref.style.outline = ""; ref.style.outlineOffset = ""; } }, 1500);
            ref.click();
          }
          return { success: true, action: "clicked", element: automationId };
        case "fill":
          if (ref) {
            const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set
              || Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
            if (setter) setter.call(ref, params.value);
            ref.dispatchEvent(new Event("input", { bubbles: true }));
            ref.dispatchEvent(new Event("change", { bubbles: true }));
          }
          return { success: true, action: "filled", element: automationId, value: params.value };
        case "select":
          if (element.onSelect) element.onSelect(params.value);
          else if (ref) { ref.value = params.value; ref.dispatchEvent(new Event("change", { bubbles: true })); }
          return { success: true, action: "selected", element: automationId, value: params.value };
        case "toggle":
          if (ref) ref.click();
          return { success: true, action: "toggled", element: automationId };
        case "scroll_to":
          if (ref) {
            ref.scrollIntoView({ behavior: "smooth", block: "center" });
            ref.style.outline = "3px solid #00d4aa";
            setTimeout(() => { if (ref) ref.style.outline = ""; }, 2000);
          }
          return { success: true, action: "scrolled_to", element: automationId };
        case "read":
          return { success: true, action: "read", element: automationId, value: element.getState?.() || ref?.textContent || "" };
        default:
          return { success: false, error: `Unknown action "${action}"` };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  return (
    <AutomationContext.Provider value={{ registerElement, updatePageState, getPageSnapshot, executeAction, currentPage }}>
      {children}
    </AutomationContext.Provider>
  );
}

export const useAutomation = () => useContext(AutomationContext);
